import { JSX, useEffect, useState } from "react";
import { useModal } from "../../hooks/useModal";
import { Modal } from "../ui/modal";
import Button from "../ui/button/Button";
import Input from "../form/input/InputField";
import Label from "../form/Label";
import { useAuth } from "../../context/AuthContext";
import { profileService } from "../../services/profileService";
import { toast } from "react-hot-toast";

// Sovereign Emerald palette
const SE = {
  paper:         '#FAF7F0',
  raised:        'rgba(255,253,246,0.97)',
  hairline:      'rgba(11,59,46,0.14)',
  hairlineSoft:  'rgba(11,59,46,0.10)',
  title:         '#0B3B2E',
  bottle:        '#1F5A47',
  brand:         '#339274',
  champagne:     '#C9A961',
  champagneWash: 'rgba(201,169,97,0.14)',
  champagneTile: 'rgba(201,169,97,0.20)',
  champagneRing: 'rgba(201,169,97,0.45)',
  hoverWash:     'rgba(11,59,46,0.05)',
  muted:         '#7C8A82',
  ink:           '#2A1F17',
};

const EditPencil = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const FieldRow = ({
  icon, label, value, fullWidth,
}: {
  icon: JSX.Element;
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}) => (
  <div
    className={`flex items-start gap-3 rounded-xl p-3 transition-all duration-150 ${fullWidth ? "lg:col-span-2" : ""}`}
    style={{ backgroundColor: SE.raised, border: `1px solid ${SE.hairline}` }}
    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = SE.champagneRing; }}
    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = SE.hairline; }}
  >
    <div
      className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg"
      style={{ backgroundColor: SE.champagneTile, color: SE.bottle, boxShadow: `inset 0 0 0 1px ${SE.champagneRing}` }}
    >
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: SE.muted }}>
        {label}
      </p>
      <p className="mt-0.5 truncate text-[13px] font-medium" style={{ color: SE.ink }}>
        {value || <span className="italic" style={{ color: SE.muted }}>Not provided</span>}
      </p>
    </div>
  </div>
);

export default function UserInfoCard() {
  const { isOpen, openModal, closeModal } = useModal();
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);

  const buildFormData = () => ({
    firstName: user?.firstName || user?.name?.split(" ")[0] || "",
    lastName:  user?.lastName  || user?.name?.split(" ").slice(1).join(" ") || "",
    email:     user?.email     || "",
    phone:     user?.phone     || user?.mobile || "",
    bio:       user?.bio       || user?.description || "",
    facebook:  user?.socialLinks?.facebook  || "",
    twitter:   user?.socialLinks?.twitter   || user?.socialLinks?.x || "",
    linkedin:  user?.socialLinks?.linkedin  || "",
    instagram: user?.socialLinks?.instagram || "",
  });

  const [formData, setFormData] = useState(buildFormData());

  useEffect(() => {
    if (isOpen) setFormData(buildFormData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await profileService.updateProfile({
        firstName: formData.firstName,
        lastName:  formData.lastName,
        email:     formData.email,
        phone:     formData.phone,
        bio:       formData.bio,
        socialLinks: {
          facebook:  formData.facebook,
          twitter:   formData.twitter,
          linkedin:  formData.linkedin,
          instagram: formData.instagram,
        },
      });
      if (result.success) {
        toast.success("Personal information updated");
        if (result.data) {
          updateUser(result.data);
        } else {
          updateUser({
            firstName: formData.firstName,
            lastName:  formData.lastName,
            name: `${formData.firstName} ${formData.lastName}`.trim(),
            email:  formData.email,
            phone:  formData.phone,
            bio:    formData.bio,
            socialLinks: { facebook: formData.facebook, twitter: formData.twitter, linkedin: formData.linkedin, instagram: formData.instagram },
          });
        }
        closeModal();
      } else { toast.error(result.message); }
    } catch { toast.error("Failed to update profile"); }
    finally { setSaving(false); }
  };

  const handleInputChange = (field: string, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const userEmail = user?.email || "";
  const userPhone = user?.phone || user?.mobile || "";
  const userBio   = user?.bio   || user?.description || "";

  const departmentRoleList: Array<{ department: string; roleName: string; active: boolean }> = (() => {
    if (Array.isArray(user?.roles) && user!.roles.length > 0) {
      return user!.roles.map((r: any) => ({
        department: r.department,
        roleName:
          (typeof r.roleId === "object" ? r.roleId?.name : null) ||
          user?.role?.name || user?.roleName || "User",
        active: r.department === user?.activeDepartment,
      }));
    }
    return [{ department: user?.activeDepartment || "", roleName: user?.role?.name || user?.role || "User", active: true }];
  })();

  return (
    <div
      className="rounded-2xl p-5 lg:p-6"
      style={{
        backgroundColor: SE.paper,
        border: `1px solid ${SE.hairline}`,
        boxShadow: '1px 0 0 rgba(201,169,97,0.06), 0 4px 16px rgba(11,59,46,0.06)',
      }}
    >
      {/* ── SECTION HEADER ──────────────────────────────────────────── */}
      <div className="mb-5 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-sm"
            style={{ backgroundImage: `linear-gradient(135deg, ${SE.bottle}, ${SE.title})` }}
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zm-4 7a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <div>
            <h4 className="text-[15px] font-bold tracking-tight" style={{ color: SE.title }}>
              Personal Information
            </h4>
            <p className="text-[11px] mt-0.5" style={{ color: SE.muted }}>
              Your public profile and contact details
            </p>
          </div>
        </div>
        <button
          onClick={openModal}
          className="inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-1.5 text-[12.5px] font-semibold transition-all duration-200"
          style={{
            backgroundColor: SE.champagneWash,
            color: SE.bottle,
            border: `1px solid ${SE.champagneRing}`,
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLElement).style.backgroundColor = SE.champagneTile;
            (e.currentTarget as HTMLElement).style.boxShadow = `0 2px 8px rgba(201,169,97,0.20)`;
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLElement).style.backgroundColor = SE.champagneWash;
            (e.currentTarget as HTMLElement).style.boxShadow = 'none';
          }}
        >
          <EditPencil />
          Edit
        </button>
      </div>

      {/* Champagne divider */}
      <div className="mb-5 h-px" style={{ background: `linear-gradient(to right, transparent, ${SE.champagneRing}, transparent)` }} />

      {/* ── FIELDS GRID ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <FieldRow
          label="First Name"
          icon={<svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zm-4 7a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
          value={user?.firstName || user?.name?.split(" ")[0] || ""}
        />
        <FieldRow
          label="Last Name"
          icon={<svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M5.121 17.804A9 9 0 1118.879 6.196 9 9 0 015.12 17.804z" /></svg>}
          value={user?.lastName || user?.name?.split(" ").slice(1).join(" ") || ""}
        />
        <FieldRow
          label="Email Address"
          icon={<svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-2 11H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2z" /></svg>}
          value={userEmail}
        />
        <FieldRow
          label="Phone"
          icon={<svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 5a2 2 0 012-2h2.28a1 1 0 01.95.68l1.5 4.49a1 1 0 01-.5 1.21l-1.7.85a11 11 0 005.52 5.52l.85-1.7a1 1 0 011.21-.5l4.49 1.5a1 1 0 01.68.95V19a2 2 0 01-2 2h-1C9.72 21 3 14.28 3 6V5z" /></svg>}
          value={userPhone}
        />
        <FieldRow
          fullWidth
          label={departmentRoleList.length > 1 ? "Department Roles" : "Role"}
          icon={<svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
          value={
            <div className="flex flex-wrap gap-1.5 mt-0.5">
              {departmentRoleList.map((dr, idx) => (
                <span
                  key={`${dr.department}-${idx}`}
                  className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10.5px] font-bold tracking-[0.07em] uppercase"
                  style={
                    dr.active
                      ? { backgroundColor: SE.champagneWash, color: SE.bottle, border: `1px solid ${SE.champagneRing}` }
                      : { backgroundColor: SE.hoverWash, color: SE.muted, border: `1px solid ${SE.hairline}` }
                  }
                >
                  <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: dr.active ? SE.champagne : SE.muted }} />
                  {dr.roleName}
                  {dr.department && <span className="opacity-60">· {dr.department}</span>}
                </span>
              ))}
            </div>
          }
        />
        <FieldRow
          fullWidth
          label="Bio"
          icon={<svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 6h16M4 12h16M4 18h10" /></svg>}
          value={userBio}
        />
      </div>

      {/* Social links */}
      {(user?.socialLinks?.facebook || user?.socialLinks?.twitter || user?.socialLinks?.x || user?.socialLinks?.linkedin || user?.socialLinks?.instagram) && (
        <div
          className="mt-4 rounded-xl p-3"
          style={{ backgroundColor: SE.raised, border: `1px solid ${SE.hairlineSoft}` }}
        >
          <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: SE.muted }}>
            Social Links
          </p>
          <div className="flex flex-wrap gap-2">
            {[
              { key: "linkedin",  url: user?.socialLinks?.linkedin },
              { key: "twitter",   url: user?.socialLinks?.twitter || user?.socialLinks?.x },
              { key: "facebook",  url: user?.socialLinks?.facebook },
              { key: "instagram", url: user?.socialLinks?.instagram },
            ]
              .filter((s) => !!s.url)
              .map((s) => (
                <a
                  key={s.key}
                  href={s.url as string}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full px-3 py-1 text-[11.5px] transition-all"
                  style={{ backgroundColor: SE.champagneTile, color: SE.bottle, border: `1px solid ${SE.champagneRing}` }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = SE.champagneWash; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = SE.champagneTile; }}
                >
                  <span className="font-semibold capitalize">{s.key}</span>
                  <span className="truncate max-w-[180px]" style={{ color: SE.muted }}>
                    {(s.url as string).replace(/^https?:\/\/(www\.)?/, "")}
                  </span>
                </a>
              ))}
          </div>
        </div>
      )}

      {/* ── EDIT MODAL ───────────────────────────────────────────────── */}
      <Modal isOpen={isOpen} onClose={closeModal} className="max-w-[700px] m-4">
        <div className="no-scrollbar relative w-full max-w-[700px] overflow-y-auto rounded-3xl bg-white p-4 dark:bg-gray-900 lg:p-11">
          <div className="px-2 pr-14">
            <h4 className="mb-2 text-2xl font-semibold text-gray-800 dark:text-white/90">
              Edit Personal Information
            </h4>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400 lg:mb-7">
              Update your details to keep your profile up-to-date.
            </p>
          </div>
          <form className="flex flex-col" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            <div className="custom-scrollbar h-[450px] overflow-y-auto px-2 pb-3">
              <div>
                <h5 className="mb-5 text-lg font-medium text-gray-800 dark:text-white/90 lg:mb-6">Personal Information</h5>
                <div className="grid grid-cols-1 gap-x-6 gap-y-5 lg:grid-cols-2">
                  <div className="col-span-2 lg:col-span-1">
                    <Label>First Name</Label>
                    <Input type="text" value={formData.firstName} onChange={(e) => handleInputChange("firstName", e.target.value)} placeholder="Enter first name" />
                  </div>
                  <div className="col-span-2 lg:col-span-1">
                    <Label>Last Name</Label>
                    <Input type="text" value={formData.lastName} onChange={(e) => handleInputChange("lastName", e.target.value)} placeholder="Enter last name" />
                  </div>
                  <div className="col-span-2 lg:col-span-1">
                    <Label>Email Address</Label>
                    <Input type="email" value={formData.email} onChange={(e) => handleInputChange("email", e.target.value)} placeholder="Enter email address" />
                  </div>
                  <div className="col-span-2 lg:col-span-1">
                    <Label>Phone</Label>
                    <Input type="tel" value={formData.phone} onChange={(e) => handleInputChange("phone", e.target.value)} placeholder="Enter phone number" />
                  </div>
                  <div className="col-span-2">
                    <Label>Bio</Label>
                    <Input type="text" value={formData.bio} onChange={(e) => handleInputChange("bio", e.target.value)} placeholder="Tell us about yourself" />
                  </div>
                </div>
              </div>
              <div className="mt-7">
                <h5 className="mb-5 text-lg font-medium text-gray-800 dark:text-white/90 lg:mb-6">Social Links</h5>
                <div className="grid grid-cols-1 gap-x-6 gap-y-5 lg:grid-cols-2">
                  <div><Label>LinkedIn</Label><Input type="text" value={formData.linkedin} onChange={(e) => handleInputChange("linkedin", e.target.value)} placeholder="https://www.linkedin.com/in/yourprofile" /></div>
                  <div><Label>X.com</Label><Input type="text" value={formData.twitter} onChange={(e) => handleInputChange("twitter", e.target.value)} placeholder="https://x.com/yourprofile" /></div>
                  <div><Label>Facebook</Label><Input type="text" value={formData.facebook} onChange={(e) => handleInputChange("facebook", e.target.value)} placeholder="https://www.facebook.com/yourprofile" /></div>
                  <div><Label>Instagram</Label><Input type="text" value={formData.instagram} onChange={(e) => handleInputChange("instagram", e.target.value)} placeholder="https://instagram.com/yourprofile" /></div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3 px-2 lg:justify-end">
              <Button size="sm" variant="outline" onClick={closeModal} disabled={saving}>Close</Button>
              <Button size="sm" onClick={handleSave} disabled={saving} type="submit">{saving ? "Saving…" : "Save Changes"}</Button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}
